import type { DataGridCellRendererProps } from '@myosh/odin-components';
import { OdinIcon, OdinIconSize, Tooltip } from '@myosh/odin-components';
import React, { useCallback, useState } from 'react';
import { useLazyGetFileUrlQuery } from '../../redux/services/file';
import { downloadFile } from '../../utils/dom-utils';

interface AttachmentInformation {
  name: string;
  sourceId: string;
}

const AttachmentsDataCell = ({ rowData }: DataGridCellRendererProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [getFileUrl] = useLazyGetFileUrlQuery();

  const handleAttachmentDownloadCallback = useCallback(
    (attachment: AttachmentInformation) => (event: React.MouseEvent) => {
      event.stopPropagation(); // We stop Propagation in order to prevend opening the record navigator on DataGrid row click

      if (!isLoading) {
        setIsLoading(true);
        getFileUrl(attachment.sourceId)
          .unwrap()
          .then((fileUrl) => {
            downloadFile(fileUrl, attachment.name);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    },
    []
  );

  return (
    // NOTE: if modifying the padding 'pl-2', please remember to update all other cells
    <div className="flex h-full w-full items-center gap-2 pl-2">
      {(rowData && rowData['attachmentInfo'] ? (rowData['attachmentInfo'] as Array<AttachmentInformation>) : []).map(
        (attachment, index) => {
          return (
            <AttachRender
              key={`sourceId-${attachment.sourceId}-index-${index}`}
              attachment={attachment}
              onDownload={handleAttachmentDownloadCallback(attachment)}
            />
          );
        }
      )}
    </div>
  );
};

export default AttachmentsDataCell;

const AttachRender = ({
  attachment,
  onDownload,
}: {
  attachment: AttachmentInformation;
  onDownload: (event: React.MouseEvent) => void;
}) => {
  return (
    <Tooltip description={String(attachment?.name)} tooltipClassName="bg-mono-1" tooltipZIndexCheck={true}>
      <div className="flex items-center gap-1 p-1 text-primary-2 hover:underline" onClick={onDownload}>
        <OdinIcon icon="Paperclip" size={OdinIconSize.ExtraSmall} />
        <span className="max-w-[100px] truncate">{attachment.name}</span>
      </div>
    </Tooltip>
  );
};
